import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CssBaseline,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Collapse,
  Divider,
  Avatar,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Dashboard as DashboardIcon,
  PeopleAlt as PeopleAltIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import AddIcon from "@mui/icons-material/Add";
import AddCardIcon from "@mui/icons-material/AddCard";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import PublishIcon from "@mui/icons-material/Publish";
import AddBoxIcon from "@mui/icons-material/AddBox";
import NotFoundPage from "../Pages/NotFoundPage";
import { useAuth } from "../Context/authContext";
import axios from "axios";

const drawerWidth = 280;

const trUpper = (text) => {
  if (!text) return "";
  // Türkçe büyük/küçük harf uyumu (i/İ)
  return text
    .toString()
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toLocaleUpperCase("tr-TR") + w.slice(1))
    .join(" ");
};

const iconSx = { minWidth: 36, color: "rgba(255,255,255,0.65)" };

const groupBtnSx = (active) => ({
  borderRadius: 2,
  px: 1.5,
  py: 0.75,
  bgcolor: active ? "rgba(99,102,241,0.15)" : "transparent",
  "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
});

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      px: 1.5,
      mb: 0.5,
      display: "block",
      color: "rgba(255,255,255,0.35)",
      textTransform: "uppercase",
      letterSpacing: 1,
      fontWeight: 700,
      fontSize: 10,
    }}
  >
    {children}
  </Typography>
);

const NavItem = ({ icon, label, active, onClick, sub = false }) => (
  <ListItemButton
    onClick={onClick}
    sx={{
      borderRadius: 2,
      px: sub ? 1.5 : 1.5,
      py: 0.75,
      bgcolor: active ? "rgba(99,102,241,0.20)" : "transparent",
      borderLeft: active ? "3px solid #6366f1" : "3px solid transparent",
      "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
      transition: "all 0.15s ease",
    }}
  >
    <ListItemIcon
      sx={{
        minWidth: 36,
        color: active ? "#a5b4fc" : "rgba(255,255,255,0.55)",
      }}
    >
      {icon}
    </ListItemIcon>
    <ListItemText
      primary={label}
      primaryTypographyProps={{
        fontSize: sub ? 13 : 14,
        fontWeight: active ? 600 : 400,
        color: active ? "#e0e7ff" : "rgba(255,255,255,0.85)",
      }}
    />
    {active && (
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: "#6366f1",
          ml: 1,
        }}
      />
    )}
  </ListItemButton>
);

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isAdmin = localStorage.getItem("role");
  const username = trUpper(localStorage.getItem("username") || "Kullanıcı");

  const { logout } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(!isMobile);

  const [usersMenuOpen, setUsersMenuOpen] = useState(false);
  const [externalPostsOpen, setExternalPostsOpen] = useState(false);
  const [blogOpen, setBlogOpen] = useState(false);

  const [authReady, setAuthReady] = useState(false);
  const authChecked = useRef(false);

  const toggleDrawer = () => setDrawerOpen((p) => !p);

  const isActive = (path) => location.pathname === path;
  const isSectionActive = (paths) => paths.some((p) => location.pathname.startsWith(p));

  // Route tanımları (daha modern/okunur)
  const routes = useMemo(
    () => ({
      dashboard: "/admin/",
      usersPeople: "/admin/kullanicilar",
      usersCompanies: "/admin/firmalar",
      externalManage: "/admin/other-posts",
      externalCreate: "/admin/create-other-post",
      externalCreateLink: "/admin/create-other-post-link",
      fastPost: "/admin/fast-post",
      blogManage: "/admin/admin-blogs",
      blogCreate: "/admin/create-blog",
      settings: "/admin/settings",
    }),
    []
  );

  const handleLogout = () => {
    logout();
    // redirect işini context yapıyorsa dokunma; değilse:
    navigate("/auth");
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) setDrawerOpen(false);
  };

  useEffect(() => {
    // responsive: mobilde kapalı başlasın
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (authChecked.current) return;
    authChecked.current = true;

    const checkAuth = async () => {
      try {
        await axios.get("https://kariyer.biyomed.net/api/admin/auth-check", {
          withCredentials: true,
        });
        setAuthReady(true);
      } catch (err) {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          localStorage.clear();
          alert("Oturumunuz sona erdi. Lütfen tekrar giriş yapın.");
          window.location.href = "/auth";
        }
      }
    };

    checkAuth();
  }, []);

  // Menülerin “aktif route”a göre otomatik açık gelmesi
  useEffect(() => {
    setUsersMenuOpen(
      isSectionActive([routes.usersPeople, routes.usersCompanies])
    );
    setExternalPostsOpen(
      isSectionActive([routes.externalManage, routes.externalCreate, routes.externalCreateLink])
    );
    setBlogOpen(isSectionActive([routes.blogManage, routes.blogCreate]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (isAdmin !== "Admin") return <NotFoundPage />;

  // authReady istersen children render’ını bekletmek için kullanılabilir
  // burada davranışı bozmamak için sadece check’i tutuyoruz
  // if (!authReady) return null;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: "#0F172A",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ gap: 1.5 }}>
          <IconButton color="inherit" onClick={toggleDrawer} edge="start">
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontWeight: 700, color: "rgba(255,255,255,0.9)" }}>
            Biyomed Kariyer Yönetim Paneli
          </Typography>

          <Tooltip title="Hesap">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: "rgba(255,255,255,0.12)",
                }}
              >
                <AccountCircleIcon />
              </Avatar>
              <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.9)" }}>
                {username}
              </Typography>
            </Box>
          </Tooltip>

          <Button
            color="inherit"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{
              ml: 1,
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 2,
              px: 1.5,
            }}
          >
            Çıkış
          </Button>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          width: !isMobile && drawerOpen ? drawerWidth : 0,
          flexShrink: 0,
          transition: (theme) =>
            theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: drawerOpen
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
            backgroundColor: "#0D1526",
            color: "rgba(255,255,255,0.92)",
            borderRight: "1px solid rgba(255,255,255,0.06)",
            backgroundImage: "linear-gradient(180deg, #0D1526 0%, #0B1220 100%)",
          },
        }}
      >
        <Toolbar />

        <Box sx={{ px: 1.5, py: 2, display: "flex", flexDirection: "column", height: "100%" }}>

          {/* Logo / Marka alanı */}
          <Box sx={{ px: 1, mb: 2 }}>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 600, fontSize: 10 }}>
              Navigasyon
            </Typography>
          </Box>

          <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>

            {/* ── Genel Bakış ── */}
            <NavItem
              icon={<DashboardIcon fontSize="small" />}
              label="Genel Bakış"
              active={isActive(routes.dashboard)}
              onClick={() => handleNavigation(routes.dashboard)}
            />

            <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.07)" }} />
            <SectionLabel>Kullanıcılar</SectionLabel>

            {/* ── Kayıtlar ── */}
            <ListItemButton
              onClick={() => setUsersMenuOpen((p) => !p)}
              sx={groupBtnSx(isSectionActive([routes.usersPeople, routes.usersCompanies]))}
            >
              <ListItemIcon sx={iconSx}><PeopleAltIcon fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Kayıtlar"
                secondary="Kişi / Firma"
                primaryTypographyProps={{ fontSize: 14, fontWeight: 500, color: "#fff" }}
                secondaryTypographyProps={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}
              />
              {usersMenuOpen ? <ExpandLess sx={{ color: "rgba(255,255,255,0.5)", fontSize: 18 }} /> : <ExpandMore sx={{ color: "rgba(255,255,255,0.5)", fontSize: 18 }} />}
            </ListItemButton>

            <Collapse in={usersMenuOpen} timeout="auto" unmountOnExit>
              <List disablePadding sx={{ pl: 1.5, display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
                <NavItem icon={<PersonIcon fontSize="small" />} label="Kişi Kayıtları" active={isActive(routes.usersPeople)} onClick={() => handleNavigation(routes.usersPeople)} sub />
                <NavItem icon={<BusinessIcon fontSize="small" />} label="Firma Kayıtları" active={isActive(routes.usersCompanies)} onClick={() => handleNavigation(routes.usersCompanies)} sub />
              </List>
            </Collapse>

            <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.07)" }} />
            <SectionLabel>İlanlar</SectionLabel>

            {/* ── Dış İlanlar ── */}
            <ListItemButton
              onClick={() => setExternalPostsOpen((p) => !p)}
              sx={groupBtnSx(isSectionActive([routes.externalManage, routes.externalCreate, routes.externalCreateLink]))}
            >
              <ListItemIcon sx={iconSx}><AddCardIcon fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Dış İlanlar"
                secondary="Yönet / Ekle"
                primaryTypographyProps={{ fontSize: 14, fontWeight: 500, color: "#fff" }}
                secondaryTypographyProps={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}
              />
              {externalPostsOpen ? <ExpandLess sx={{ color: "rgba(255,255,255,0.5)", fontSize: 18 }} /> : <ExpandMore sx={{ color: "rgba(255,255,255,0.5)", fontSize: 18 }} />}
            </ListItemButton>

            <Collapse in={externalPostsOpen} timeout="auto" unmountOnExit>
              <List disablePadding sx={{ pl: 1.5, display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
                <NavItem icon={<BusinessCenterIcon fontSize="small" />} label="İlanları Yönet" active={isActive(routes.externalManage)} onClick={() => handleNavigation(routes.externalManage)} sub />
                <NavItem icon={<AddIcon fontSize="small" />} label="Yeni İlan Ekle" active={isActive(routes.externalCreate)} onClick={() => handleNavigation(routes.externalCreate)} sub />
                <NavItem icon={<AddBoxIcon fontSize="small" />} label="Link ile İlan Ekle" active={isActive(routes.externalCreateLink)} onClick={() => handleNavigation(routes.externalCreateLink)} sub />
              </List>
            </Collapse>

            {/* ── Hızlı İlan ── */}
            <NavItem
              icon={<NoteAddIcon fontSize="small" />}
              label="Hızlı İlan"
              active={isActive(routes.fastPost)}
              onClick={() => handleNavigation(routes.fastPost)}
            />

            <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.07)" }} />
            <SectionLabel>İçerik</SectionLabel>

            {/* ── Blog ── */}
            <ListItemButton
              onClick={() => setBlogOpen((p) => !p)}
              sx={groupBtnSx(isSectionActive([routes.blogManage, routes.blogCreate]))}
            >
              <ListItemIcon sx={iconSx}><PublishIcon fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Blog"
                secondary="Yönet / Ekle"
                primaryTypographyProps={{ fontSize: 14, fontWeight: 500, color: "#fff" }}
                secondaryTypographyProps={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}
              />
              {blogOpen ? <ExpandLess sx={{ color: "rgba(255,255,255,0.5)", fontSize: 18 }} /> : <ExpandMore sx={{ color: "rgba(255,255,255,0.5)", fontSize: 18 }} />}
            </ListItemButton>

            <Collapse in={blogOpen} timeout="auto" unmountOnExit>
              <List disablePadding sx={{ pl: 1.5, display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
                <NavItem icon={<BusinessCenterIcon fontSize="small" />} label="Blogları Yönet" active={isActive(routes.blogManage)} onClick={() => handleNavigation(routes.blogManage)} sub />
                <NavItem icon={<AddIcon fontSize="small" />} label="Yeni Blog Ekle" active={isActive(routes.blogCreate)} onClick={() => handleNavigation(routes.blogCreate)} sub />
              </List>
            </Collapse>

            <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.07)" }} />

            {/* ── Ayarlar ── */}
            <NavItem
              icon={<SettingsIcon fontSize="small" />}
              label="Ayarlar"
              active={isActive(routes.settings)}
              onClick={() => handleNavigation(routes.settings)}
            />
          </List>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "background.default",
          minHeight: "100vh",
          p: { xs: 2, md: 3 },
          overflowX: "auto",
          minWidth: 0,
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default AdminLayout;